import React, { useCallback, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Body1, Body2, Paper } from "@activecollab/components";
import PropTypes from "prop-types";
import NotFoundSlate from "~/components/EmptySlate/NotFoundSlate";
import { useGlobalPrivateChannelListener } from "~/components/Hooks/useGlobalPrivateChannelListener";
import { NoteRealTime } from "~/components/Note/NoteRealTime";
import { Page } from "~/components/Page";
import { Resolver } from "~/components/Resolver.jsx";
import { TrashSlate } from "~/components/TrashSlate/TrashSlate";
import { useNotebook } from "~/repository";
import { fetchObjectLastVisitTimestamp } from "~/store/actions/actions.lastVisit";
import { fetchNote } from "~/store/actions/actions.notes";
import { fetchProject } from "~/store/actions/actions.projects";
import { getNoteById } from "~/store/selectors/selector.notes";
import { getLoggedUser } from "~/store/selectors/selector.users";
import { useNoteActions } from "./useNoteActions";

const NotFound = () => {
  const { t } = useTranslation();
  return (
    <Paper className="tw-flex tw-flex-col tw-h-full tw-mb-8">
      <NotFoundSlate
        header={t("This note is not found")}
        pageName={t("projects page")}
        pageLink="/projects"
      />
    </Paper>
  );
};

export const NoteTrashSlate = ({ id, projectId, children }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const note = useSelector((state) => getNoteById(state, id));
  const { data: notebook, isLoading } = useNotebook(projectId, note?.notebook_id);
  const isNotebookTrashed = notebook?.single.is_trashed;
  const [notFoundShow, setNotFoundShow] = useState(() => {
    if (note) {
      return note.project_id !== projectId; //case when you go back in browser and note is moved to another project
    }
    return false;
  });
  const resolveDependencies = useCallback(async () => {
    await dispatch(fetchObjectLastVisitTimestamp("Note", id));
    await dispatch(fetchProject(projectId));
    await dispatch(fetchNote(projectId, id)).then(({ error }) => {
      if (error) {
        throw error;
      }
    });
  }, [dispatch, id, projectId]);

  const { handleRestore, handleDelete, canRestore } = useNoteActions(id, projectId);

  const loggedUser = useSelector(getLoggedUser);

  const type = "Note";

  useGlobalPrivateChannelListener("DataObjectDeleted", (data) => {
    if (data.type === type && data.id === id) {
      setNotFoundShow(true);
    }
  });

  if (notFoundShow) {
    return <NotFound />;
  }

  return (
    <Resolver
      key={`note-${id}-${projectId}`}
      callback={resolveDependencies}
      loadingComponent={<Page.Loader />}
      errorComponent={<NotFound />}
    >
      <TrashSlate
        type={type}
        className="tw-h-full tw-items-center"
        subtitle={
          <>
            <Body1
              align="center"
              color="primary"
              className="tw-text-center tw-text-theme-900"
            >
              <Trans>This {type.toLowerCase()} is deleted</Trans>.
            </Body1>

            {canRestore && !isNotebookTrashed && !isLoading ? (
              <Body2
                color="primary"
                align="center"
                className="tw-mt-2"
              >
                {t("If you want to work on it again, recover it")}.
              </Body2>
            ) : null}
          </>
        }
        isTrashed={note?.is_trashed}
        trashedOn={note?.trashed_on}
        trashedById={note?.trashed_by_id}
        createdById={note?.created_by_id}
        createdByName={note?.created_by_name}
        createdOn={note?.created_on}
        canRestore={canRestore && !isNotebookTrashed && !isLoading}
        canPermanentlyDelete={loggedUser.class === "Owner"}
        onRestore={handleRestore}
        onPermanentlyDelete={handleDelete}
      >
        {children}
      </TrashSlate>
      <NoteRealTime
        id={id}
        projectId={projectId}
        noteName={note?.name}
        noteBody={note?.body}
        notebookId={note?.notebook_id}
      />
    </Resolver>
  );
};

NoteTrashSlate.propTypes = {
  id: PropTypes.number.isRequired,
  projectId: PropTypes.number.isRequired,
  children: PropTypes.node,
};
